/**
 * Hours and clock-time arithmetic shared by every timesheet surface.
 *
 * The server enforces the same rules in normalise_timesheet_entry(); these
 * exist so a form can refuse bad input before the round trip and show the
 * same message, and so the Day view can compute an end from a start and a
 * duration exactly the way the trigger will. Keep the two in step.
 */

/** Smallest bookable unit: a quarter of an hour. */
export const HOURS_STEP = 0.25;

export const QUARTER_HOUR_MESSAGE = 'Hours must be in 15-minute steps (0.25, 0.5, 0.75, ...).';

/** True when `hours` sits on a quarter-hour boundary (to float tolerance). */
export const isQuarterHour = (hours: number): boolean => {
  const quarters = hours * 4;
  return Math.abs(quarters - Math.round(quarters)) < 0.001;
};

/** Round to the nearest quarter hour. */
export const roundToQuarter = (hours: number): number => Math.round(hours * 4) / 4;

/**
 * Parse what someone typed into a plain hours field. Returns null for blank
 * or unparseable input; the caller decides whether blank means "delete".
 */
export const parseHours = (text: string): number | null => {
  const trimmed = text.trim();
  if (!trimmed) return null;
  const value = Number(trimmed);
  return Number.isFinite(value) ? value : null;
};

/** `1.5` → "1.50", `8` → "8.00". Two decimals so columns line up. */
export const formatHours = (hours: number): string => hours.toFixed(2);

/** `1.5` → "1.5", `8` → "8", `1.25` → "1.25". For chips and cells. */
export const formatHoursShort = (hours: number): string => {
  const rounded = Math.round(hours * 100) / 100;
  return Number.isInteger(rounded) ? String(rounded) : String(rounded).replace(/0+$/, '');
};

// ---------------------------------------------------------------------------
// Durations: "1.5", "1:30", "1h30", "1h 30m", "90m", "2h"
// ---------------------------------------------------------------------------

export type ParsedDuration = number | null | 'invalid';

/**
 * Parse a typed duration into decimal hours rounded to the nearest quarter.
 * Blank → null. Anything unreadable, zero, or over 24 h → 'invalid'.
 */
export const parseDurationInput = (text: string): ParsedDuration => {
  const s = text.trim().toLowerCase().replace(/\s+/g, '');
  if (!s) return null;

  let hours: number | null = null;

  const plain = s.match(/^(\d+(?:\.\d+)?)h?$/);
  const colon = s.match(/^(\d+):(\d{1,2})$/);
  const hm = s.match(/^(\d+)h(\d{1,2})m?$/);
  const mOnly = s.match(/^(\d+)m$/);

  if (plain) hours = Number(plain[1]);
  else if (colon) hours = Number(colon[1]) + Number(colon[2]) / 60;
  else if (hm) hours = Number(hm[1]) + Number(hm[2]) / 60;
  else if (mOnly) hours = Number(mOnly[1]) / 60;

  if (hours === null || !Number.isFinite(hours)) return 'invalid';
  const rounded = roundToQuarter(hours);
  if (rounded <= 0 || rounded > 24) return 'invalid';
  return rounded;
};

// ---------------------------------------------------------------------------
// Clock times: stored as "HH:mm:ss" (Postgres time), edited as "HH:mm"
// ---------------------------------------------------------------------------

export type ParsedTime = string | null | 'invalid';

const pad2 = (n: number): string => String(n).padStart(2, '0');

/** "08:30:00" | "8:30" → "08:30". Null passes through. */
export const formatTime = (time: string | null | undefined): string => {
  if (!time) return '';
  const [h, m] = time.split(':');
  return `${pad2(Number(h))}:${pad2(Number(m ?? 0))}`;
};

/** "08:30" → minutes since midnight. */
export const timeToMinutes = (time: string): number => {
  const [h, m] = time.split(':');
  return Number(h) * 60 + Number(m ?? 0);
};

/** Minutes since midnight → "HH:mm". Clamped to the day. */
export const minutesToTime = (minutes: number): string => {
  const clamped = Math.max(0, Math.min(24 * 60, Math.round(minutes)));
  return `${pad2(Math.floor(clamped / 60) % 24)}:${pad2(clamped % 60)}`;
};

/**
 * Parse a typed clock time into "HH:mm". Accepts "8", "830", "0830", "8:30",
 * "8.30", "1330", "1.30pm", "8am", "12p". Blank → null. Unreadable → 'invalid'.
 * Displays and stores 24-hour time.
 */
export const parseTimeInput = (text: string): ParsedTime => {
  let s = text.trim().toLowerCase().replace(/\s+/g, '');
  if (!s) return null;

  let meridiem: 'am' | 'pm' | null = null;
  const mer = s.match(/(am|pm|a|p)$/);
  if (mer) {
    meridiem = mer[1].startsWith('a') ? 'am' : 'pm';
    s = s.slice(0, -mer[1].length);
  }

  let hour: number;
  let minute: number;

  const sep = s.match(/^(\d{1,2})[:.](\d{1,2})$/);
  if (sep) {
    hour = Number(sep[1]);
    minute = Number(sep[2]);
  } else if (/^\d{1,2}$/.test(s)) {
    hour = Number(s);
    minute = 0;
  } else if (/^\d{3}$/.test(s)) {
    hour = Number(s[0]);
    minute = Number(s.slice(1));
  } else if (/^\d{4}$/.test(s)) {
    hour = Number(s.slice(0, 2));
    minute = Number(s.slice(2));
  } else {
    return 'invalid';
  }

  if (meridiem === 'pm' && hour < 12) hour += 12;
  if (meridiem === 'am' && hour === 12) hour = 0;

  if (hour < 0 || hour > 24 || minute < 0 || minute > 59) return 'invalid';
  if (hour === 24 && minute > 0) return 'invalid';
  return `${pad2(hour)}:${pad2(minute)}`;
};

/**
 * A lunch break, which is not worked: the employee's usual one, or a day's
 * own (timesheet_day_lunches). `start` is "HH:mm"; `minutes` 0 means no
 * break is deducted.
 */
export interface LunchBreak {
  start: string;
  minutes: number;
}

/** Minutes of [start, end] that fall inside the lunch break. */
export const lunchOverlapMinutes = (start: string, end: string, lunch: LunchBreak | null | undefined): number => {
  if (!lunch || lunch.minutes <= 0) return 0;
  const lunchStart = timeToMinutes(formatTime(lunch.start));
  const lunchEnd = lunchStart + lunch.minutes;
  return Math.max(0, Math.min(timeToMinutes(end), lunchEnd) - Math.max(timeToMinutes(start), lunchStart));
};

/**
 * Hours worked between two "HH:mm" times: the span less the part inside the
 * lunch break, rounded to the nearest quarter, exactly as
 * normalise_timesheet_entry() derives them. Null when nothing is worked.
 */
export const hoursBetween = (start: string, end: string, lunch?: LunchBreak | null): number | null => {
  const minutes = timeToMinutes(end) - timeToMinutes(start) - lunchOverlapMinutes(start, end, lunch);
  if (minutes <= 0) return null;
  const hours = roundToQuarter(minutes / 60);
  return hours > 0 ? hours : null;
};

/** The lunch window as minutes since midnight, or null when there is none. */
const lunchWindow = (lunch: LunchBreak | null | undefined): { start: number; end: number } | null => {
  if (!lunch || lunch.minutes <= 0) return null;
  const start = timeToMinutes(formatTime(lunch.start));
  return { start, end: start + lunch.minutes };
};

/**
 * Start plus a worked duration → "HH:mm": the end is pushed out by whatever
 * lunch the span crosses, so 3 h from 11:00 ends at 15:00 over a 12:00 lunch.
 * Closed form (not iterative): the work before lunch is whatever fits, the
 * rest lands after it. A start inside the break counts from its end. Clamped
 * to midnight.
 */
export const addHoursToTime = (start: string, hours: number, lunch?: LunchBreak | null): string => {
  const work = hours * 60;
  const s = timeToMinutes(formatTime(start));
  const win = lunchWindow(lunch);
  if (!win) return minutesToTime(s + work);
  const from = s >= win.start && s < win.end ? win.end : s;
  if (from >= win.end) return minutesToTime(from + work);
  const before = win.start - from;
  return minutesToTime(work <= before ? from + work : win.end + (work - before));
};

/**
 * End less a worked duration → the "HH:mm" it must have started at, the
 * mirror of addHoursToTime: the work after lunch is whatever fits, the rest
 * comes off before it. An end inside the break counts back from its start.
 */
export const subtractHoursFromTime = (end: string, hours: number, lunch?: LunchBreak | null): string => {
  const work = hours * 60;
  const e = timeToMinutes(formatTime(end));
  const win = lunchWindow(lunch);
  if (!win) return minutesToTime(e - work);
  const to = e > win.start && e <= win.end ? win.start : e;
  if (to <= win.start) return minutesToTime(to - work);
  const after = to - win.end;
  return minutesToTime(work <= after ? to - work : win.start - (work - after));
};

/** A start that falls inside the lunch break moves to the end of it. */
export const startAfterLunch = (start: string, lunch: LunchBreak | null | undefined): string => {
  if (!lunch || lunch.minutes <= 0) return start;
  const s = timeToMinutes(formatTime(start));
  const lunchStart = timeToMinutes(formatTime(lunch.start));
  const lunchEnd = lunchStart + lunch.minutes;
  return s >= lunchStart && s < lunchEnd ? minutesToTime(lunchEnd) : formatTime(start);
};

/** "08:30" → "8:30", for compact display. */
export const displayTime = (time: string | null | undefined): string => {
  if (!time) return '';
  const [h, m] = formatTime(time).split(':');
  return `${Number(h)}:${m}`;
};

/** Minutes between the end of one timed entry and the start of the next. */
export const gapMinutes = (prevEnd: string, nextStart: string): number =>
  timeToMinutes(formatTime(nextStart)) - timeToMinutes(formatTime(prevEnd));

/** "35 min" / "1 h 15 min" */
export const formatMinutes = (minutes: number): string => {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m} min`;
  if (m === 0) return `${h} h`;
  return `${h} h ${m} min`;
};
